import i18next from 'i18next'
import {
  convertTwoDecimalStringToBig,
  DECIMAL_PLACES,
  getBigZero,
  safeBigToNumber,
} from './bigJSHelper'

/**
 * Formats a currency string with financial precision using Big.js.
 * Safely handles invalid or empty input, strips unnecessary decimals,
 * and formats according to locale and ISO currency code.
 *
 * @param raw - Raw numeric string (e.g. "100.00")
 * @param currency - ISO currency code (e.g. "USD", "EUR", "JPY")
 * @param locale - BCP 47 locale (defaults to current i18next language)
 * @returns Formatted currency string (e.g. "$100", "C$100.00")
 *
 * SECURITY NOTE:
 * - Passing `raw` as a string is safe because it’s never executed or evaluated as code.
 * - The input is parsed strictly by `Big.js`, which only accepts valid numeric-like strings.
 * - Any invalid or malicious input (e.g. "abc", "1e999999", "DROP TABLE") is caught and replaced with `0`,
 *   ensuring no crashes or injection risks.
 * - Although using `toNumber()` can risk losing precision with very large numbers,
 *   it’s safe and necessary here because `Intl.NumberFormat` requires a native number type.
 *   Since this function is for *display purposes*, not financial computation, the conversion is appropriate.
 */
export function getLocaleCurrencyForAmount(
  raw: string,
  currency: string,
  locale: string = i18next.language
): string {
  let amount: Big

  try {
    // Big.js safely parses numeric-like strings; invalid input throws an error
    amount = convertTwoDecimalStringToBig(raw)
  } catch {
    // Default to 0 for malformed input
    amount = getBigZero()
  }

  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: DECIMAL_PLACES,
    maximumFractionDigits: DECIMAL_PLACES,
  })

  // TODO: Temporarily removing currency symbols to only display formatted numbers instead WF-1081245
  return (
    formatter?.format(safeBigToNumber(amount)).replace(/[^0-9.,-]+/g, '') ??
    `{value}\u00A0${currency}`
  )
}

export default getLocaleCurrencyForAmount
